// Sugalarm — cycle 451.1: the ONE predicate for "alarm audio is sounding",
// and the route rule that keeps a control underneath it.
//
// THE INVARIANT THIS EXISTS FOR (cycle 81, restated): at least one alarm
// control is on screen and hit-testable whenever alarm audio is sounding.
//
// Cycle 451 is what made the gap reachable. Restoring the app chrome on the
// treating screen means a caregiver holding a ten-minute window can now
// reach History, Log, Household and Settings from a bedside tablet, at
// night. But `useAlarmSounds` is mounted ONCE in the authenticated shell and
// takes the episode, so the loop resumes wherever the user is standing,
// while `AlarmSilenceGuard` and all three alarm banners mount only inside
// `NowScreen`. The audio had a route-independent driver; the controls did
// not. Cycle 451 reported that split rather than changing it, and named this
// as the next cycle's first item.
//
// ⚠ ONE PREDICATE, TWO CALLERS. `audibleAlarmLevel` IS the derivation the
// audio bridge already used to decide what to sound — it was the body of
// `useAlarmSounds`'s `targetLevel`, lifted here unchanged — and the route
// rule reads the same function. Cycle 451's own lesson, one cycle earlier,
// was `treatingSurfaceMounted`: two gates for one fact drift, and a mirror
// that drifts on an alarm path is the one deciding whether anybody can hear
// it. Nothing else may re-derive "is it sounding" from episode fields.
//
// ⚠ THE PREVIEW TERM IS NOT IN THE BRIDGE'S GATE, AND MUST NEVER BE. A
// Settings test-alarm must not be able to suppress a real alarm loop, so
// `previewActive` is a term of `alarmAudioSounding` (the ROUTE rule) only —
// never of `audibleAlarmLevel` (the AUDIO gate). The two functions differ by
// exactly that term, in that direction.

import type { AlertLevel } from "./types";

/** Everything the audio gate is allowed to look at. Flat value object, no
 *  clocks, no refs, no fetches — so the derivation is testable without a
 *  renderer, in the style of `alarmStandDown.ts`. */
export interface AlarmAudibleInput {
  /** `settings.alerts_enabled` — the bridge's master switch. */
  enabled: boolean;
  /** The bridge's arm gate: an active member is resolved and the latest
   *  reading is fresh (`activeMemberId != null && readingFresh`). */
  audioArmable: boolean;
  /** The episode's own audio flag, `audioOnFor(...)` in `alarmStandDown.ts`
   *  = `phase === "active" && !silenced && !treating && !standDown`. This is
   *  the single field the route rule is allowed to read about an episode,
   *  and it is the one the bridge gates on. A snoozed episode, a stopped
   *  one, an open declared treating window and a quiet window are all
   *  already false here — the exclusions the rule needs come free, and by
   *  construction rather than by a second list. */
  audioOn: boolean;
  /** Cycle 403: a `call_quiet` deadline is in force for THIS session's own
   *  account — we are on the phone with it and its in-app audio is cut. */
  callQuiet: boolean;
  /** Cycle 410: the level the bridge would actually SOUND, which is not
   *  always `episode.level`. Under a lead window on a bound urgent row this
   *  is the open `low` sibling's level (a descent) or null (a cold
   *  crossing: nothing sounds on this device until the deadline). */
  audibleLevel: AlertLevel | null;
}

/** The low-side level the audio bridge would drive right now, or null.
 *
 *  ⚠ LOW SIDE ONLY, AND THAT IS NOT A NARROWING INTRODUCED HERE. Cycle 232
 *  made the continuous loop low-side only: `urgent_high` lost its sustained
 *  loop and highs use the hourly chime cadence off `socket.lastAlertEvent`
 *  instead. `startUrgentHighLoop` survives in the hook's returned API with
 *  NO production caller (verified repo-wide) — the test console drives it.
 *  So "a high-side alarm is sounding a loop with no control under it" is not
 *  a reachable state, and the house rule that decides the rest is the
 *  standing one: lows kill, highs do not. */
export function audibleAlarmLevel(
  s: AlarmAudibleInput,
): "low" | "urgent_low" | null {
  if (!s.enabled || !s.audioArmable || !s.audioOn || s.callQuiet) return null;
  if (s.audibleLevel === "low" || s.audibleLevel === "urgent_low") {
    return s.audibleLevel;
  }
  return null;
}

/** Inputs to the ROUTE rule.
 *
 *  ⚠ IT TAKES THE BRIDGE'S OUTPUT, NOT THE EPISODE. The only alarm fact in
 *  here is `audibleLevel` — the value `useAlarmSounds` computed with
 *  `audibleAlarmLevel` above and is driving its elements from this very
 *  render. The route rule therefore reads no episode field of its own, not
 *  even `audioOn`, and there is no second place where "is it sounding" could
 *  be assembled differently. */
export interface AlarmSoundingInput {
  /** `alarms.audibleAlarmLevel` — the bridge's own current audible level. */
  audibleLevel: "low" | "urgent_low" | null;
  /** Kiosk surface: urgent low gets the repeating cue rather than a
   *  one-shot (`urgentLowPlaybackMode`). */
  kioskUrgentRepeat: boolean;
  /** A Settings "Test alarms" preview is running (`alarmPreview.ts`).
   *
   *  Already false for the real episode by construction — `PreviewOverlay`
   *  builds its OWN synthetic episode, and `previewSound` plays through a
   *  dedicated element that never touches an alarm element and never sets
   *  `loopActive`. The term is carried explicitly anyway so the exclusion is
   *  a stated property with a test on it. */
  previewActive: boolean;
}

/** Is the bridge driving SUSTAINED audio right now?
 *
 *  ⚠ THIS IS THE DISTINCTION THE PIN TURNS ON, AND IT IS NOT COSMETIC.
 *  `audibleAlarmLevel` is what the bridge would DRIVE, which is not the same
 *  as what is audible: off the kiosk, cycle 374 made urgent low a ~3 s
 *  ONE-SHOT, and the level stays set for the rest of the episode after it has
 *  played. Pinning on the level alone would hold a phone on `/now` for a
 *  whole un-snoozed urgent low on the strength of three seconds of sound —
 *  and, when cycle 381's `inAppCueQuiet` skips the cue entirely (a voice call
 *  is in flight) while still marking the level driven, would pin it having
 *  made NO sound at all. Only two playbacks actually sustain:
 *
 *    - `low`        — a continuous loop, every surface;
 *    - `urgent_low` — the kiosk's repeating cue only.
 *
 *  The one-shot is handled on its own EDGE instead (see
 *  `useAlarmRouteReturn`): it brings the app to `/now` once, when the cue
 *  actually fires, and never pins. */
export function alarmAudioContinuous(s: AlarmSoundingInput): boolean {
  if (s.audibleLevel === "low") return true;
  return s.audibleLevel === "urgent_low" && s.kioskUrgentRepeat;
}

/** "Sustained alarm audio is sounding, and it is the real thing." The
 *  route rule's pinning predicate, and the only one it may use. */
export function alarmAudioSounding(s: AlarmSoundingInput): boolean {
  if (s.previewActive) return false;
  return alarmAudioContinuous(s);
}

/** Routes the return-to-Now rule never acts on, inside the authenticated
 *  shell.
 *
 *  ⚠ THE PAIRING FAMILY IS EXEMPT ON PURPOSE. Interrupting a handshake
 *  mid-flow can strand a device unpaired, and an unpaired kiosk is itself an
 *  alarm-DELIVERY hazard — a worse one than a missing on-screen control,
 *  because the push rail, the calls and the texts reach the household
 *  regardless of which route this SPA is showing. `/login` is listed for
 *  completeness: the authenticated shell carries the route, and a redirect
 *  off a sign-in form would be its own defect.
 *
 *  Cold-boot `/pair` is not reachable by this rule at all — it renders in
 *  `AppInner`, outside the authenticated shell, so every unauthenticated
 *  route is excluded by mounting rather than by this list. */
//
// ⚠ CYCLE 453.2 (operator decision 2, 2026-09-28): `/reset-password` and
// `/forgot-password` are exempt too, and exactly those two. A signed-in
// session opening a reset link now renders it INSIDE the shell, where the
// alarm sound and the off-Now alarm control (`CompactAlarmControl`) cover it
// — pulling the caregiver off a half-typed new password would lose it, and
// the control is already on screen. The list stays closed: a new entry is an
// operator decision (`resetRoutesInShell.test.ts` pins it exactly).
export const ROUTE_RETURN_EXEMPT_PATHS: readonly string[] = [
  "/login",
  "/pair",
  "/reset-password",
  "/forgot-password",
];

/** The route decision. `true` means "take the app to /now, where the alarm
 *  controls are".
 *
 *  Deliberately NOT edge-triggered: it is a function of (sounding, path), so
 *  it fires on the rising edge AND again if the user navigates away while
 *  the audio is still sounding. Once the audio stops — Snooze, Stop where it
 *  is allowed, a server recovery, a resume handled — it goes false and the
 *  user may navigate freely. */
/** ⚠ PREFIX MATCH, NOT EQUALITY. `/pair` covers `/pair-device` and
 *  `/pair-approve` — the whole pairing family — and survives a trailing
 *  slash, which React Router tolerates and exact matching would have let
 *  through. `AvatarNudge` already uses `startsWith("/pair")` for the same
 *  family, so this is the idiom the codebase had. The `-` arm is what
 *  carries `/pair-device` and `/pair-approve`; the `/` arm carries a
 *  trailing slash and any future nested pairing route. */
export function routeReturnExempt(pathname: string): boolean {
  return ROUTE_RETURN_EXEMPT_PATHS.some(
    (p) =>
      pathname === p ||
      pathname.startsWith(p + "/") ||
      pathname.startsWith(p + "-"),
  );
}

export function shouldReturnToNow(s: {
  alarmSounding: boolean;
  pathname: string;
}): boolean {
  if (!s.alarmSounding) return false;
  if (s.pathname === "/now") return false;
  return !routeReturnExempt(s.pathname);
}
